import type { EnemyKind, FruitKind, SimulationEvent } from '../sim/types';

export class AudioManager {
  private context: AudioContext | null = null;
  private musicTimer: number | null = null;
  private musicStep = 0;
  private musicVolume = 0.38;
  private sfxVolume = 0.72;

  async unlock(): Promise<void> {
    if (!this.context) this.context = new AudioContext();
    if (this.context.state === 'suspended') await this.context.resume();
  }

  setVolumes(music: number, sfx: number): void {
    this.musicVolume = Math.max(0, Math.min(1, music));
    this.sfxVolume = Math.max(0, Math.min(1, sfx));
  }

  /**
   * Original looping arcade theme, synthesised in eighth notes: a bouncing
   * pentatonic melody with an octave sparkle, a walking bass on every beat, and
   * a soft brush hat on the off-beats. The last bar of each four-bar phrase
   * lifts an octave so the loop never feels static. Warden globes swap to a
   * minor mode for the same motif.
   */
  startMusic(levelId: number): void {
    this.stopMusic();
    this.musicStep = 0;
    const roots = [261.63, 293.66, 246.94, 277.18, 233.08, 311.13, 293.66, 261.63, 246.94, 220.0];
    const root = roots[(levelId - 1) % roots.length] ?? 261.63;
    const boss = levelId === 5 || levelId === 10;
    const scale = boss ? [0, 3, 5, 7, 10, 12, 15] : [0, 2, 4, 7, 9, 12, 14];
    const melody = [0, 2, 3, 2, 4, 3, 2, -1, 1, 3, 5, 4, 2, 1, 0, -1];
    const bassLine = [0, 4, 2, 3];
    const pitch = (semitones: number): number => root * Math.pow(2, semitones / 12);

    this.musicTimer = window.setInterval(() => {
      const index = this.musicStep % melody.length;
      const phraseBar = Math.floor(this.musicStep / melody.length) % 4;
      const lift = phraseBar === 3 ? 12 : 0;
      const note = melody[index] ?? -1;
      if (note >= 0) {
        const degree = (scale[note] ?? 0) + lift;
        this.tone(pitch(degree), 0.2, 'triangle', 0.11, 0, 'music');
        this.tone(pitch(degree + 12), 0.11, 'sine', 0.035, 0, 'music', 0.012);
      }
      if (index % 4 === 0) {
        const degree = scale[bassLine[(index / 4) % bassLine.length] ?? 0] ?? 0;
        this.tone(pitch(degree - 12), 0.34, 'triangle', 0.1, 0, 'music');
        this.noise(0.05, 0.05, 'music');
      } else if (index % 2 === 1) {
        this.noise(0.026, 0.016, 'music');
      }
      this.musicStep += 1;
    }, 214);
  }

  stopMusic(): void {
    if (this.musicTimer !== null) window.clearInterval(this.musicTimer);
    this.musicTimer = null;
  }

  playEvent(event: SimulationEvent, combo = 0): void {
    switch (event.type) {
      case 'move': this.tone(120, 0.035, 'triangle', 0.035); break;
      case 'core': {
        const root = 440 * 2 ** (Math.min(combo, 7) / 12);
        this.tone(root, 0.17, 'sine', 0.2, 0.12);
        this.tone(root * 2, 0.11, 'triangle', 0.08, 0.04);
        this.tone(root * 3, 0.055, 'square', 0.022, 0.03, 'sfx', 0.018);
        this.noise(0.035, 0.025);
        this.sweep(root * 0.7, root * 1.35, 0.12, 0.055);
        this.fruitAccent(event.fruitKind, root);
        break;
      }
      case 'final-core': {
        const root = 440 * 2 ** (Math.min(combo, 7) / 12);
        this.fruitAccent(event.fruitKind, root, 0.8);
        this.chord([523, 659, 784], 0.42, 0.2);
        break;
      }
      case 'fruit-wave': this.sweep(260, 880, 0.3, 0.14); this.chord([392, 523, 659, 784], 0.42, 0.18); break;
      case 'fruit-warning': this.fruitBehaviorCue(event.fruitKind, event.label, true); break;
      case 'fruit-move': this.fruitBehaviorCue(event.fruitKind, event.label, false); break;
      case 'fruit-teleport':
        this.sweep(940, 170, 0.22, 0.055);
        this.tone(330, 0.08, 'sine', 0.035, 0, 'sfx', 0.24);
        this.tone(660, 0.14, 'sine', 0.04, 0.04, 'sfx', 0.29);
        break;
      case 'wall-place': this.sweep(190, 560, 0.16, 0.15); break;
      case 'wall-reject': this.tone(110, 0.09, 'square', 0.06, -0.1); break;
      case 'wall-crack': this.noise(0.1, 0.15); break;
      case 'wall-shatter': this.noise(0.18, 0.28); this.chord([110, 220, 440], 0.18, 0.12); break;
      case 'enemy-alert':
        // `amount: 0` marks a hunter standing down, so it gets a deflating
        // cue instead of the aggressive per-kind sting.
        if (event.amount === 0) {
          this.sweep(430, 150, 0.34, 0.075);
          this.noise(0.22, 0.03);
        } else {
          this.enemyAlert(event.enemyKind);
        }
        break;
      case 'enemy-stun': this.sweep(620, 180, 0.22, 0.15); break;
      case 'shift-warning': this.chord([220, 277], 0.25, 0.16); break;
      case 'shift': this.sweep(90, 920, 0.6, 0.18); break;
      case 'portal-open': this.chord([261, 392, 523, 659], 0.7, 0.14); break;
      case 'damage': this.noise(0.16, 0.2); this.tone(82, 0.2, 'sawtooth', 0.12, -0.2); break;
      case 'thaw':
        this.noise(0.1, 0.09);
        this.sweep(420, 74, 0.48, 0.11);
        this.tone(660, 0.08, 'sine', 0.045, 0, 'sfx', 0.2);
        this.tone(440, 0.12, 'sine', 0.04, -0.04, 'sfx', 0.31);
        break;
      case 'revive': this.chord([392, 494, 587], 0.35, 0.12); break;
      case 'special': this.sweep(300, 740, 0.22, 0.13); break;
      case 'shard': this.chord([740, 932, 1110], 0.55, 0.13); break;
      case 'boss-phase': this.chord([98, 147, 196], 0.8, 0.16); break;
      case 'victory': this.chord([392, 494, 587, 784], 0.9, 0.17); break;
      case 'failure': this.sweep(160, 52, 0.7, 0.12); break;
    }
  }

  private fruitAccent(kind: FruitKind | undefined, root: number, strength = 1): void {
    const gain = (value: number): number => value * strength;
    if (!kind) return;
    switch (kind) {
      case 'strawberry':
        this.tone(root * 1.5, 0.14, 'sine', gain(0.034), 0.04, 'sfx', 0.018);
        break;
      case 'greenApple':
        this.tone(root * 0.75, 0.055, 'square', gain(0.012), -0.05);
        this.tone(root * 1.25, 0.09, 'triangle', gain(0.026), 0, 'sfx', 0.018);
        break;
      case 'orange':
        this.tone(root * 1.25, 0.08, 'triangle', gain(0.026), 0, 'sfx', 0.012);
        this.tone(root * 2.5, 0.1, 'sine', gain(0.022), 0.03, 'sfx', 0.04);
        break;
      case 'pear':
        this.tone(root * 0.75, 0.22, 'sine', gain(0.038), -0.03);
        break;
      case 'cherries':
        this.tone(root * 1.75, 0.075, 'sine', gain(0.03), -0.02, 'sfx', 0.005);
        this.tone(root * 2.1, 0.075, 'sine', gain(0.027), 0.02, 'sfx', 0.052);
        break;
      case 'grapes':
        this.tone(root * 1.8, 0.08, 'sine', gain(0.026), 0, 'sfx', 0.005);
        this.tone(root * 1.5, 0.09, 'sine', gain(0.024), -0.02, 'sfx', 0.038);
        this.tone(root * 1.25, 0.11, 'triangle', gain(0.022), -0.04, 'sfx', 0.072);
        break;
      case 'watermelon':
        this.tone(root * 0.58, 0.09, 'sine', gain(0.04), -0.06);
        this.tone(root * 2.4, 0.045, 'square', gain(0.012), 0.04, 'sfx', 0.038);
        break;
      case 'pineapple':
        this.tone(root * 1.25, 0.09, 'triangle', gain(0.023), 0, 'sfx', 0.006);
        this.tone(root * 1.6, 0.1, 'triangle', gain(0.021), 0.02, 'sfx', 0.035);
        this.tone(root * 2, 0.13, 'sine', gain(0.02), 0.04, 'sfx', 0.07);
        break;
      case 'banana':
        this.tone(root * 0.9, 0.17, 'sine', gain(0.034), -0.04, 'sfx', 0.008);
        this.tone(root * 1.2, 0.14, 'triangle', gain(0.022), 0.02, 'sfx', 0.06);
        break;
      case 'blueberries':
        this.tone(root * 1.5, 0.07, 'sine', gain(0.026), 0, 'sfx', 0.004);
        this.tone(root * 1.75, 0.07, 'sine', gain(0.024), 0.02, 'sfx', 0.032);
        this.tone(root * 2.1, 0.09, 'sine', gain(0.022), 0.04, 'sfx', 0.06);
        break;
      default: {
        const exhaustive: never = kind;
        return exhaustive;
      }
    }
  }

  private fruitBehaviorCue(kind: FruitKind | undefined, label: string | undefined, warning: boolean): void {
    if (kind === 'pineapple' || label === 'TAKEOFF' || label === 'LEAF ROTOR') {
      if (warning) {
        this.tone(260, 0.045, 'triangle', 0.026, 0, 'sfx', 0.005);
        this.tone(390, 0.045, 'triangle', 0.025, 0, 'sfx', 0.075);
        this.tone(520, 0.06, 'triangle', 0.024, 0.02, 'sfx', 0.145);
      } else {
        this.noise(0.09, 0.035);
        this.sweep(210, 760, 0.24, 0.06);
      }
      return;
    }
    if (kind === 'cherries') {
      this.tone(740, 0.08, 'sine', 0.025, -0.04);
      this.tone(1_110, 0.11, 'sine', 0.022, 0.05, 'sfx', 0.06);
      return;
    }
    if (kind === 'pear' || label === 'RUN' || label === 'PEAR DASH') {
      this.tone(warning ? 240 : 310, 0.055, 'square', 0.018, warning ? -0.04 : 0.08);
      if (!warning) this.tone(465, 0.065, 'triangle', 0.022, 0.04, 'sfx', 0.045);
      return;
    }
    if (kind === 'strawberry') {
      this.tone(warning ? 360 : 420, 0.07, 'sine', 0.022, warning ? -0.03 : 0.03);
      if (!warning) this.noise(0.025, 0.012);
    }
  }

  private enemyAlert(kind: EnemyKind | undefined): void {
    if (!kind) {
      this.chord([98, 147, 196], 0.28, 0.14);
      return;
    }
    switch (kind) {
      case 'tracer':
        this.tone(620, 0.07, 'square', 0.035, 0, 'sfx', 0.005);
        this.tone(780, 0.1, 'sine', 0.04, 0.03, 'sfx', 0.07);
        break;
      case 'rammer':
        this.tone(92, 0.24, 'sawtooth', 0.06, -0.08);
        this.noise(0.07, 0.07);
        break;
      case 'seer':
        this.chord([415, 622, 831], 0.26, 0.13);
        break;
      case 'wisp':
        this.sweep(240, 960, 0.28, 0.1);
        this.tone(1_140, 0.13, 'sine', 0.035, 0.04, 'sfx', 0.17);
        break;
      case 'scoop':
        // Cherry Scoop: a wet gulp under a rising two-note battle cry.
        this.tone(150, 0.13, 'sawtooth', 0.075, -0.05);
        this.noise(0.06, 0.05);
        this.tone(494, 0.1, 'square', 0.045, 0.02, 'sfx', 0.08);
        this.tone(740, 0.19, 'square', 0.05, 0.05, 'sfx', 0.17);
        this.sweep(560, 1_180, 0.2, 0.055);
        break;
      default: {
        const exhaustive: never = kind;
        return exhaustive;
      }
    }
  }

  private tone(
    frequency: number,
    duration: number,
    type: OscillatorType,
    gainValue: number,
    detune = 0,
    bus: 'music' | 'sfx' = 'sfx',
    delay = 0,
  ): void {
    const context = this.context;
    if (!context || context.state !== 'running') return;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const startAt = context.currentTime + Math.max(0, delay);
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, startAt);
    oscillator.detune.setValueAtTime(detune * 100, startAt);
    gain.gain.setValueAtTime(0.0001, startAt);
    const volume = bus === 'music' ? this.musicVolume : this.sfxVolume;
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, gainValue * volume), startAt + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(startAt);
    oscillator.stop(startAt + duration + 0.02);
  }

  private sweep(start: number, end: number, duration: number, gainValue: number): void {
    const context = this.context;
    if (!context || context.state !== 'running') return;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(start, context.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(end, context.currentTime + duration);
    gain.gain.setValueAtTime(gainValue * this.sfxVolume, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + duration);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + duration + 0.02);
  }

  private chord(frequencies: readonly number[], duration: number, gain: number): void {
    frequencies.forEach((frequency, index) => this.tone(frequency, duration + index * 0.03, 'sine', gain / frequencies.length, index * 0.02));
  }

  private noise(duration: number, gainValue: number, bus: 'music' | 'sfx' = 'sfx'): void {
    const context = this.context;
    if (!context || context.state !== 'running') return;
    const length = Math.max(1, Math.floor(context.sampleRate * duration));
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let index = 0; index < data.length; index += 1) data[index] = (Math.random() * 2 - 1) * (1 - index / data.length);
    const source = context.createBufferSource();
    const gain = context.createGain();
    source.buffer = buffer;
    gain.gain.value = gainValue * (bus === 'music' ? this.musicVolume : this.sfxVolume);
    source.connect(gain).connect(context.destination);
    source.start();
  }
}
